/**
 * Lazy, singleton ffmpeg.wasm loader.
 *
 * The core (`ffmpeg-core.js` / `ffmpeg-core.wasm`) is self-hosted under `/ffmpeg`
 * (copied from `@ffmpeg/core` by webpack) rather than fetched from a public CDN,
 * so the viewer keeps working offline / behind strict egress and satisfies CSP
 * `connect-src 'self'`. The core is materialized into blob URLs before `load()`,
 * which is why CSP `script-src` includes `blob:`.
 *
 * ffmpeg is only ever loaded when an endoscopy / video series is opened — it is
 * dynamically imported by the caller so it stays out of the main bundle.
 */
import { FFmpeg } from '@ffmpeg/ffmpeg';
import { toBlobURL } from '@ffmpeg/util';
import { vlog } from './debug';
const CORE_BASE = '/ffmpeg';
let loadPromise = null;
function getFfmpeg() {
    if (loadPromise)
        return loadPromise;
    loadPromise = (async () => {
        const ffmpeg = new FFmpeg();
        // ffmpeg's internal log is normally swallowed; surface it only when video
        // diagnostics are enabled (see ./debug — localStorage 'debug:video').
        ffmpeg.on('log', ({ type, message }) => vlog('[ffmpeg]', type, message));
        vlog('[ffmpeg] loading core from', CORE_BASE);
        const [coreURL, wasmURL] = await Promise.all([
            toBlobURL(`${CORE_BASE}/ffmpeg-core.js`, 'text/javascript'),
            toBlobURL(`${CORE_BASE}/ffmpeg-core.wasm`, 'application/wasm'),
        ]);
        vlog('[ffmpeg] core blobs ready; calling load()');
        await ffmpeg.load({ coreURL, wasmURL });
        vlog('[ffmpeg] load() succeeded');
        return ffmpeg;
    })();
    // Reset the cached promise on failure so a later view can retry. ffmpeg.wasm
    // can reject with a non-Error (e.g. a string), so always log it explicitly —
    // otherwise the failure is invisible (empty message, no console output).
    loadPromise.catch((e) => {
        console.error('[ffmpeg] core failed to load:', e);
        loadPromise = null;
    });
    return loadPromise;
}
// A single ffmpeg.wasm instance cannot run two operations concurrently (shared
// virtual FS + one worker), so all work is serialized through this promise
// chain. Poster extraction and MP4 remux can be requested in parallel (strip +
// viewport); this keeps them from interleaving writeFile/exec calls.
let chain = Promise.resolve();
export function withFfmpeg(fn) {
    const run = chain.then(async () => {
        const ffmpeg = await getFfmpeg();
        return fn(ffmpeg);
    });
    // Keep the chain alive even if this op rejects, so later ops still run.
    chain = run.catch(() => undefined);
    return run;
}
